import React, { useCallback, useEffect, useRef, useState } from 'react';
import Icon from './Icon';
import EditorTexto from './EditorTexto';
import { inserirMarcador, novoIdAnexo, removerMarcador } from '../services/textoComAnexos';
import { alternarTag, TAGS } from '../services/formatacaoTexto';

// ─────────────────────────────────────────────────────────────────────────────
// O campo onde o atendimento é escrito.
//
// Antes eram três blocos: a caixa de texto, um painel "Imagens" com dropzone e
// lista de legendas, e um painel "Áudio" com gravador. Três lugares para contar
// uma coisa só — e nenhum deles sabia em que ponto do relato o print entrava.
//
// Agora é um campo só:
//   · a imagem entra NO TEXTO, onde o cursor está (colar, arrastar ou pelo
//     ícone). A ordem em que aparece é a ordem em que sai no documento;
//   · o áudio é um ícone de microfone no canto do campo, e o que foi gravado
//     vira uma linha discreta embaixo — não um card.
//
// A legenda de cada imagem continua existindo: ela vira o nome do arquivo no
// destino e a legenda no PDF. Só que agora é pedida ao clicar na imagem, e não
// numa lista à parte.
// ─────────────────────────────────────────────────────────────────────────────

/** Segundos → "MM:SS". */
function cronometro(segundos) {
  const mm = String(Math.floor(segundos / 60)).padStart(2, '0');
  const ss = String(segundos % 60).padStart(2, '0');
  return `${mm}:${ss}`;
}

/** Lê um arquivo de imagem e devolve o objeto que a formalização usa. */
function lerImagem(file) {
  return new Promise((resolve, reject) => {
    if (!file || !String(file.type || '').startsWith('image/')) { resolve(null); return; }
    const leitor = new FileReader();
    leitor.onload = (ev) => {
      const dataUrl = ev.target.result;
      resolve({
        id: novoIdAnexo(),
        dataUrl,
        base64: String(dataUrl).split(',')[1],
        nome: file.name || 'imagem.png',
        legenda: '',
      });
    };
    leitor.onerror = reject;
    leitor.readAsDataURL(file);
  });
}

export default function CampoAnotacoes({
  valor,
  aoMudar,
  // Barra de negrito/itálico/sublinhado. Só faz sentido no texto que vai para
  // o MySuite, que renderiza essas tags — nas anotações, não.
  comFormatacao = false,
  imagens = [],
  aoMudarImagens,
  audio,
  aoMudarAudio,
  aoAvisar,
  placeholder = 'O que o cliente relatou, o que foi verificado e o que foi feito',
  alto = false,
  desabilitado = false,
}) {
  const editorRef = useRef(null);
  const inputImagem = useRef(null);
  const inputAudio = useRef(null);

  const [gravando, setGravando] = useState(false);
  const [segundos, setSegundos] = useState(0);
  const [urlPreview, setUrlPreview] = useState(null);
  const [ampliada, setAmpliada] = useState(null);

  // Imagens recém-anexadas que ainda esperam legenda.
  //
  // É uma FILA, e não um id só, porque dá para anexar várias de uma vez
  // (selecionar cinco prints, colar duas seguidas). Uma por vez, na ordem em
  // que entraram: sem a fila, o último modal aberto apagaria os anteriores e as
  // outras imagens ficariam sem legenda.
  const [filaLegenda, setFilaLegenda] = useState([]);

  // Onde o cursor deve voltar quando a fila de legendas terminar. O foco não
  // pode ir para o editor enquanto o modal da legenda está aberto — ver
  // `anexarImagem`.
  const cursorAposAnexo = useRef(null);

  const gravadorRef = useRef(null);
  const streamRef = useRef(null);
  const pedacosRef = useRef([]);
  const intervaloRef = useRef(null);

  // Mapa por id: é o que o editor usa para trocar o marcador pela figura.
  const porId = {};
  imagens.forEach((img) => { if (img?.id) porId[img.id] = img; });

  // ── imagens ────────────────────────────────────────────────────────────────

  const anexarImagem = useCallback(async (file) => {
    if (desabilitado) return;
    // Sem quem guarde as imagens (o ticket novo, por exemplo), o campo é só
    // texto e áudio.
    if (!aoMudarImagens) { aoAvisar?.('Aqui não dá para anexar imagem.'); return; }
    let lida;
    try {
      lida = await lerImagem(file);
    } catch (e) {
      aoAvisar?.('Não consegui ler essa imagem.');
      return;
    }
    if (!lida) { aoAvisar?.('Só dá para anexar imagem aqui.'); return; }

    // Onde o cursor estava. Clicar no ícone tira o foco do campo, por isso o
    // editor guarda a última posição que viu — senão toda imagem cairia no fim.
    const posicao = editorRef.current?.cursor();
    const { texto, cursor } = inserirMarcador(valor, lida.id, posicao);

    aoMudarImagens([...imagens, lida]);
    aoMudar(texto);
    // A legenda é pedida AGORA, e não depois: é o único momento em que a pessoa
    // tem na cabeça o que aquela imagem mostra. Deixar para o fim significa, na
    // prática, documento com cinco figuras sem descrição nenhuma.
    setFilaLegenda(fila => [...fila, lida.id]);

    // O foco NÃO volta para o editor agora — ele é do modal da legenda, que
    // abre em seguida.
    //
    // Aqui morava um `setTimeout(() => editorRef.current?.focar(cursor), 0)`.
    // O modal recebia o foco no campo da legenda (autoFocus) e, um tique
    // depois, esse timeout o devolvia ao editor: quem digitava a legenda estava
    // na verdade escrevendo dentro das anotações, atrás do modal. A legenda
    // ficava vazia, o "Confirmar" continuava desabilitado e a imagem nunca
    // saía da fila — parecia que anexar não funcionava.
    //
    // O cursor fica guardado e volta quando a fila esvazia, que é quando a
    // pessoa de fato retoma a escrita.
    cursorAposAnexo.current = cursor;
  }, [valor, imagens, aoMudar, aoMudarImagens, aoAvisar, desabilitado]);

  /**
   * Colar ou arrastar no campo. O tipo do arquivo decide o destino: imagem
   * entra no texto, áudio vira o áudio do atendimento. É o caminho descoberto
   * sozinho — ninguém precisa saber que existe um ícone para isso.
   */
  const anexarArquivo = useCallback((file) => {
    if (!file) return;
    if (String(file.type || '').startsWith('audio/') && aoMudarAudio) {
      aoMudarAudio({ blob: file, nome: file.name || 'audio.webm' });
      return;
    }
    anexarImagem(file);
  }, [anexarImagem, aoMudarAudio]);

  /**
   * Devolve o foco ao editor, na posição de depois da imagem — o que deixa a
   * pessoa continuar escrevendo embaixo da figura. Só quando não há mais
   * legenda a pedir: com a fila cheia, o próximo modal é que manda na tela.
   *
   * No timeout porque o DOM do editor é remontado quando o texto novo chega;
   * focar antes disso perderia a posição.
   */
  const retomarEscrita = (fila) => {
    if (fila.some(id => porId[id])) return;
    const cursor = cursorAposAnexo.current;
    cursorAposAnexo.current = null;
    setTimeout(() => editorRef.current?.focar(cursor ?? undefined), 0);
  };

  const removerImagem = (id) => {
    aoMudarImagens(imagens.filter(img => img.id !== id));
    aoMudar(removerMarcador(valor, id));
    setAmpliada(null);
    const resto = filaLegenda.filter(x => x !== id);
    setFilaLegenda(resto);
    retomarEscrita(resto);
  };

  const mudarLegenda = (id, legenda) =>
    aoMudarImagens(imagens.map(img => (img.id === id ? { ...img, legenda } : img)));

  // A primeira imagem da fila que ainda existe. O `porId` é a fonte da verdade:
  // uma imagem removida enquanto esperava sai da fila sozinha.
  const idPedindo = filaLegenda.find(id => porId[id]) || null;
  const pedindoLegenda = idPedindo ? porId[idPedindo] : null;
  const restantesNaFila = filaLegenda.filter(id => porId[id] && id !== idPedindo).length;

  const confirmarLegenda = () => {
    if (!idPedindo) return;
    const resto = filaLegenda.filter(x => x !== idPedindo);
    setFilaLegenda(resto);
    retomarEscrita(resto);
  };

  // Clicar numa imagem do texto abre a ampliação — é lá que se dá a legenda e
  // se remove. O clique não pode chegar ao contenteditable, senão o cursor
  // pousa dentro da figura.
  const aoClicarNoTexto = (e) => {
    const id = e.target?.getAttribute?.('data-anexo');
    if (!id || !porId[id]) return;
    e.preventDefault();
    setAmpliada(porId[id]);
  };

  // ── áudio ──────────────────────────────────────────────────────────────────

  /** Solta o microfone. Sem isto o indicador de gravação fica aceso no sistema. */
  const pararMicrofone = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
  }, []);

  // O preview é um object URL: precisa ser revogado ao trocar de áudio, senão
  // cada gravação deixa um blob pendurado na memória até fechar o app.
  useEffect(() => {
    if (!audio?.blob) { setUrlPreview(null); return undefined; }
    const url = URL.createObjectURL(audio.blob);
    setUrlPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [audio]);

  // Limpeza ao desmontar: para o cronômetro e solta o microfone.
  useEffect(() => () => {
    clearInterval(intervaloRef.current);
    pararMicrofone();
  }, [pararMicrofone]);

  const pararGravacao = () => {
    if (gravadorRef.current && gravadorRef.current.state === 'recording') {
      gravadorRef.current.stop();
    }
    setGravando(false);
  };

  const iniciarGravacao = async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      aoAvisar?.('Gravação de áudio não é suportada neste ambiente.');
      return;
    }
    try {
      streamRef.current = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (e) {
      aoAvisar?.('Não foi possível acessar o microfone (permissão negada?).');
      return;
    }

    // Chromium e Electron gravam em webm/opus; a lista cobre o que houver.
    const mime = ['audio/webm;codecs=opus', 'audio/webm', 'audio/ogg']
      .find(m => window.MediaRecorder && MediaRecorder.isTypeSupported(m)) || '';

    pedacosRef.current = [];
    const gravador = new MediaRecorder(streamRef.current, mime ? { mimeType: mime } : undefined);
    gravadorRef.current = gravador;

    gravador.addEventListener('dataavailable', (ev) => {
      if (ev.data && ev.data.size) pedacosRef.current.push(ev.data);
    });

    gravador.addEventListener('stop', () => {
      pararMicrofone();
      clearInterval(intervaloRef.current);
      const tipo = gravador.mimeType || 'audio/webm';
      const ext = tipo.includes('ogg') ? 'ogg' : 'webm';
      const blob = new Blob(pedacosRef.current, { type: tipo });
      if (blob.size) aoMudarAudio({ blob, nome: `gravacao_${Date.now()}.${ext}` });
    });

    gravador.start();
    setSegundos(0);
    setGravando(true);
    intervaloRef.current = setInterval(() => setSegundos(s => s + 1), 1000);
  };

  const limparAudio = () => {
    if (gravadorRef.current?.state === 'recording') {
      try { gravadorRef.current.stop(); } catch (e) { /* já parou */ }
    }
    pararMicrofone();
    clearInterval(intervaloRef.current);
    setGravando(false);
    setSegundos(0);
    aoMudarAudio(null);
  };

  // ── formatação ─────────────────────────────────────────────────────────────

  /**
   * Aplica (ou tira) negrito, itálico e sublinhado no que está selecionado.
   * O MySuite renderiza `<b>`, `<i>` e `<u>` na interação — é assim que se
   * destaca a mensagem de erro exata dentro do texto.
   */
  const formatar = (tag) => {
    const sel = editorRef.current?.selecao() || { inicio: valor.length, fim: valor.length };
    const r = alternarTag(valor, sel.inicio, sel.fim, tag);
    aoMudar(r.texto);
    // O DOM é remontado quando o texto novo chega; devolver a seleção depois
    // disso é o que permite aplicar duas formatações seguidas.
    setTimeout(() => editorRef.current?.selecionar(r.inicio, r.fim), 0);
  };

  // ── tela ───────────────────────────────────────────────────────────────────

  return (
    <>
      <div className="ct-wrap" onClickCapture={aoClicarNoTexto}>
        <EditorTexto
          ref={editorRef}
          className={alto ? 'ct-alto' : ''}
          value={valor}
          imagens={porId}
          onChange={e => aoMudar(e.target.value)}
          onArquivo={anexarArquivo}
          placeholder={placeholder}
          disabled={desabilitado}
        />

        <div className="ct-borda">
          {comFormatacao && TAGS.map(({ tag, rotulo, titulo }) => (
            <button
              key={tag}
              type="button"
              className="ct-btn ct-btn-tag"
              // mousedown com preventDefault preserva a seleção: o clique
              // normal tiraria o foco do campo antes de a função rodar.
              onMouseDown={(e) => { e.preventDefault(); formatar(tag); }}
              disabled={desabilitado}
              title={`${titulo} (aplica e remove)`}
              aria-label={titulo}
            >
              {rotulo}
            </button>
          ))}
          {aoMudarImagens && (
            <button
              type="button"
              className="ct-btn"
              onClick={() => inputImagem.current?.click()}
              disabled={desabilitado}
              title="Inserir imagem no ponto do texto (ou cole com Ctrl+V)"
              aria-label="Inserir imagem"
            >
              <Icon name="image" size={13} />
            </button>
          )}

          {/* Áudio tem dois caminhos, e os dois ficam à vista: gravar na hora
              (microfone) e anexar um arquivo que já existe (clipe). Arrastar ou
              colar um áudio no campo continua funcionando. */}
          {aoMudarAudio && (
            <>
              <button
                type="button"
                className={`ct-btn ${gravando ? 'ct-gravando' : ''}`}
                onClick={gravando ? pararGravacao : iniciarGravacao}
                disabled={desabilitado}
                title={gravando ? 'Parar a gravação' : 'Gravar áudio agora'}
                aria-label={gravando ? 'Parar a gravação' : 'Gravar áudio'}
              >
                <Icon name={gravando ? 'stop' : 'mic'} size={13} />
              </button>
              <button
                type="button"
                className="ct-btn"
                onClick={() => inputAudio.current?.click()}
                // Durante a gravação não faz sentido: o arquivo escolhido
                // seria substituído pelo que estiver sendo gravado.
                disabled={desabilitado || gravando}
                title="Anexar um arquivo de áudio (ou arraste um para o campo)"
                aria-label="Anexar arquivo de áudio"
              >
                <Icon name="paperclip" size={13} />
              </button>
            </>
          )}
        </div>

        <input
          ref={inputImagem}
          type="file"
          accept="image/*"
          multiple
          style={{ display: 'none' }}
          onChange={async (e) => {
            const arquivos = Array.from(e.target.files || []);
            e.target.value = '';
            // Em série: cada uma precisa entrar depois da anterior, senão as
            // duas caem na mesma posição do cursor e a ordem se perde.
            for (const f of arquivos) {
              // eslint-disable-next-line no-await-in-loop
              await anexarImagem(f);
            }
          }}
        />
        <input
          ref={inputAudio}
          type="file"
          accept="audio/*"
          style={{ display: 'none' }}
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) aoMudarAudio({ blob: f, nome: f.name });
            e.target.value = '';
          }}
        />
      </div>

      {(gravando || audio) && (
        <div className="ct-rodape">
          {gravando && (
            <span className="ct-cronometro">
              <Icon name="mic" size={12} /> gravando · {cronometro(segundos)}
            </span>
          )}
          {audio && !gravando && (
            <span className="ct-audio">
              <Icon name="play" size={12} />
              <span className="ct-audio-nome" title={audio.nome}>{audio.nome}</span>
              {urlPreview && <audio controls src={urlPreview} />}
              <button
                type="button"
                className="anexo-remover"
                onClick={limparAudio}
                aria-label="Remover o áudio"
              >
                <Icon name="x" size={11} />
              </button>
            </span>
          )}
        </div>
      )}

      {/* Legenda da imagem recém-anexada.
          Fica na frente da ampliação de propósito: enquanto houver imagem sem
          legenda na fila, é dela que a tela trata. Não fecha por clique fora
          nem por Esc — as duas saídas são dar a legenda ou tirar a imagem. */}
      {pedindoLegenda && (
        <div className="lightbox">
          <div className="lb-caixa" onClick={e => e.stopPropagation()}>
            <img src={pedindoLegenda.dataUrl} alt="Imagem recém-anexada" />
            <div className="lb-acoes" style={{ flexWrap: 'wrap' }}>
              <div
                className="campo-dica"
                style={{ flexBasis: '100%', marginBottom: 6 }}
              >
                O que esta imagem mostra?{restantesNaFila > 0 ? ` (faltam mais ${restantesNaFila})` : ''}
                {' '}A legenda aparece sob a figura no documento do cliente.
              </div>
              <input
                className="img-legenda"
                placeholder="Ex.: mensagem de erro ao confirmar a nota"
                value={pedindoLegenda.legenda || ''}
                onChange={e => mudarLegenda(pedindoLegenda.id, e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter' && String(pedindoLegenda.legenda || '').trim()) {
                    e.preventDefault();
                    confirmarLegenda();
                  }
                }}
                autoFocus
              />
              <button className="btn btn-danger btn-sm" onClick={() => removerImagem(pedindoLegenda.id)}>
                <Icon name="trash" size={13} /> Descartar
              </button>
              <button
                className="btn btn-primary btn-sm"
                onClick={confirmarLegenda}
                disabled={!String(pedindoLegenda.legenda || '').trim()}
              >
                <Icon name="check" size={13} /> Confirmar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Ampliação: é aqui que se revê a legenda e se remove a imagem. A legenda
          vira o nome do arquivo no destino e a legenda no documento. */}
      {!pedindoLegenda && ampliada && (
        <div className="lightbox" onClick={() => setAmpliada(null)}>
          <div className="lb-caixa" onClick={e => e.stopPropagation()}>
            <img src={ampliada.dataUrl} alt={ampliada.legenda || 'Imagem anexada'} />
            <div className="lb-acoes">
              <input
                className="img-legenda"
                placeholder="Legenda (vira o nome do arquivo e a legenda no documento)"
                value={porId[ampliada.id]?.legenda || ''}
                onChange={e => mudarLegenda(ampliada.id, e.target.value)}
                autoFocus
              />
              <button className="btn btn-danger btn-sm" onClick={() => removerImagem(ampliada.id)}>
                <Icon name="trash" size={13} /> Remover
              </button>
              <button className="btn btn-ghost btn-sm" onClick={() => setAmpliada(null)}>
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
